import {
  Link,
  Text,
} from '@chakra-ui/react';
import {
  useInvestManagement,
} from '@src/modules/performance/context/reports-api-hooks';
import { selectedDatabaseIdState, currentDatabaseSelector } from '@transport-insights/uikit';
import { useRecoilValue } from 'recoil';
import LineRangeAreaChart from '@src/modules/performance/components/charts/LineRangeAreaChart';
import { CHART_COLOURS } from '@src/modules/performance/shared/constants';
import { filterChartDataByName } from '@src/modules/performance/shared/helpers';
import ReportSection from './ReportSection';

const informationSource = {
  title: 'Management',
  content: [
    {
      title: 'Investment management, network and property management',
      description:
  <Text fontSize="inherit" mb={2}>
    A combination of (a) Investment management and transport planning expenditure including activity management planning, programme business case development, programme management, regional land transport planning management, studies, strategies & models, transport model development and (b) network and asset management, and property management expenditure including work category 151 for the general management and control of the road network and management of road assets, work category 161 provides for costs associated with the management of property purchased for future roading, and work category 171 for grants to approved organisations at the discretion of NZTA (as investor).
  </Text>,
      source:
  <Text fontSize="inherit" as="span">
    Sourced from
    {' '}
    <Link href="https://www.nzta.govt.nz/planning-and-investment/learning-and-resources/transport-data/data-and-tools/" isExternal>NZTA Data and Tools</Link>
  </Text>,
    },
  ],
};

function TransportProgrammeDeliveryManagement() {
  const rcaId = useRecoilValue(selectedDatabaseIdState);
  const currentDatabase = useRecoilValue(currentDatabaseSelector);
  const { data: investManagement, isLoading: isLoadingInvestManagement } = useInvestManagement();
  const isLoading = isLoadingInvestManagement;
  const isRegionNationalOrStateHighways = rcaId >= 1000 || rcaId === 81;
  const isNational = rcaId === 1000;
  let chartFilters = [];
  if (isNational) {
    chartFilters = ['Peer', 'Percentile'];
  } else if (isRegionNationalOrStateHighways) {
    chartFilters = ['Peer'];
  }

  const investManagementYears = [...new Set(investManagement?.map((item) => item.Year))];
  const investManagementData = [{
    name: currentDatabase?.name,
    type: 'line',
    data: investManagement?.map((item) => ({ x: item.Year, y: item.FundValue / 1000000 })),
    color: CHART_COLOURS.blue,
  }, {
    name: 'Peer Group',
    type: 'line',
    data: investManagement?.map((item) => ({ x: item.Year, y: item.PeerGroupValue / 1000000 })),
    color: CHART_COLOURS.black,
  }, {
    name: '25-75th Percentile',
    type: 'rangeArea',
    data: investManagement?.map((item) => ({ x: item.Year, y: [item.LowPercentile / 1000000, item.HighPercentile / 1000000] })),
    color: CHART_COLOURS.lightgrey,
  }];

  // Filter out the Peer Group and Percentile data if it's a national report, and Peer Group if it's a region or state highways report
  const investManagementChartSeries = filterChartDataByName(
    investManagementData,
    chartFilters,
  );

  const markers = isRegionNationalOrStateHighways ? {
    size: [3, 0],
    hover: {
      size: 6,
    },
  } : {
    size: [3, 3, 0],
    hover: {
      size: 6,
    },
  };

  const stroke = isRegionNationalOrStateHighways ? {
    curve: 'straight',
    width: [2, 0],
  } : {
    curve: 'straight',
    width: [2, 2, 0],
  };

  return (
    <ReportSection
      title="Management"
      informationSource={informationSource}
      isLoading={isLoading || !investManagement}
      lockYear={investManagement?.[0]?.LockYear}
    >
      <LineRangeAreaChart
        title={['Investment management, network', 'and property management']}
        categories={investManagementYears}
        series={investManagementChartSeries}
        type="rangeArea"
        width="280"
        height="280"
        stroke={stroke}
        markers={markers}
        prefix="$"
        suffix="M"
        decimalPoints={1}
        yAxisDecimalPoints={1}
      />
    </ReportSection>
  );
}

export default TransportProgrammeDeliveryManagement;
